import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import DrugCalcPublic from './public/DrugCalcPublic';
import RemsQuickAccess from './rems/RemsQuickAccess';
import AuthenticatedTenantBanner from './brand/AuthenticatedTenantBanner';
import MemberMobileVerificationBoundary from './auth/MemberMobileVerificationBoundary';
import NativeVersionGate from './version/NativeVersionGate';
import './theme-contrast.css';
import { isNativeShell } from './auth/authRedirect';
import { installNativeApiBridge } from './native/nativeRuntime';
import { installNativeLifecycleBridge } from './native/nativeLifecycle';

installNativeApiBridge();
installNativeLifecycleBridge();

const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
const showPublicDrugCalc = !isNativeShell() && normalizedPath === '/drugcalc';
const showRemsQuickAccess = /^\/rems-access(?:\/|$)/i.test(normalizedPath);

if (showPublicDrugCalc) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/drugcalc-manifest.json?v=1');

  const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitle) appleTitle.setAttribute('content', 'SMFD Drug Calc');

  const appName = document.querySelector('meta[name="application-name"]');
  if (appName) appName.setAttribute('content', 'SMFD Drug Calculator');

  document.title = 'SMFD Drug Calculator';
}

const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement);

function replaceText(selector, from, to) {
  const element = rootElement.querySelector(selector);
  if (element?.textContent?.trim() === from) element.textContent = to;
}

function replaceVisibleText(rootNode, replacements) {
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(node => {
    let value = node.nodeValue || '';
    replacements.forEach(([from, to]) => { value = value.replace(from, to); });
    node.nodeValue = value;
  });
}

function applyPlatformCopy() {
  replaceVisibleText(rootElement, [
    [/SMFD Line Medic/g, 'Line Medic'],
    [/SMFD · WILDLAND MEDICAL/g, 'LINE MEDIC PLATFORM'],
    [/Line Medic Sign In/g, 'Line Medic Member Sign In'],
    [/Adult Drug Calculator/g, 'Drug Calculator'],
    [/Adult weight-based doses, draw volumes, and push-dose epi preparation — works offline\./g, 'Adult and pediatric weight-based doses, draw volumes, and protocol-linked medication calculations — works offline.'],
    [/Offline Sacramento County adult weight-based dosing and SMFD Line Medic draw volumes\./g, 'Offline Sacramento County adult and pediatric medication calculations with configured Line Medic draw volumes.'],
  ]);

  replaceText('.smfd-brand-kicker', 'SMFD · WILDLAND MEDICAL', 'LINE MEDIC PLATFORM');
  replaceText('.smfd-signed-in', 'Operational resources and field support', 'Wildland medical operations & field support');

  rootElement.querySelectorAll('.smfd-brand-mark img, .member-auth-mark').forEach(image => { image.alt = 'Line Medic'; });

  const fieldKicker = [...rootElement.querySelectorAll('.section-kicker')]
    .find(element => element.textContent?.trim() === 'FIELD OPERATIONS');
  const fieldHeading = fieldKicker?.closest('.section-heading');
  const fieldTitle = fieldHeading?.querySelector('h2');
  const fieldDescription = fieldHeading?.querySelector('p');

  if (fieldTitle?.textContent?.trim() === 'Operational tools') fieldTitle.textContent = 'Field resources';
  if (fieldDescription?.textContent?.trim() === 'The things you are most likely to need before, during and after an assignment.') {
    fieldDescription.textContent = 'Gear, communications, EMS references, medication calculations, and weather tools for line assignments.';
  }

  const authCard = rootElement.querySelector('.member-auth-card');
  if (authCard) {
    replaceVisibleText(authCard, [
      [/approved district account/gi, 'authorized organization account'],
      [/district account/gi, 'organization account'],
      [/district email or REMS username/gi, 'organization email'],
      [/district email/gi, 'organization email'],
      [/Use your district @metrofire\.ca\.gov email or the approved REMS team username\./g, 'Use the organization email configured for this Line Medic deployment.'],
      [/Use your district @metrofire\.ca\.gov email\./g, 'Use the organization email configured for this Line Medic deployment.'],
    ]);

    authCard.querySelectorAll('input').forEach(input => {
      if (/metrofire\.ca\.gov/i.test(input.placeholder || '')) input.placeholder = 'name@agency.gov';
    });
  }
}

const copyObserver = new MutationObserver(applyPlatformCopy);
copyObserver.observe(rootElement, { childList: true, subtree: true });

root.render(
  showPublicDrugCalc
    ? <DrugCalcPublic />
    : (
      <NativeVersionGate>
        {showRemsQuickAccess
          ? <RemsQuickAccess />
          : (
            <MemberMobileVerificationBoundary>
              <AuthenticatedTenantBanner />
              <App />
            </MemberMobileVerificationBoundary>
          )}
      </NativeVersionGate>
    ),
);
window.requestAnimationFrame(applyPlatformCopy);

if (!isNativeShell() && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then(registration => registration.update().catch(() => {}))
      .catch(() => {});
  });
}
